import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ListingCard from "@/components/listing/ListingCard";
import { Button } from "@/components/ui/button";
import {
  Loader2, Store, Users, MapPin, BadgeCheck, Heart, Settings,
  MessageCircle, Instagram, Package, Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm5.8 14.1c-.24.68-1.4 1.3-1.94 1.34-.5.05-1.1.2-3.7-.78-3.1-1.2-5.1-4.35-5.26-4.55-.15-.2-1.25-1.66-1.25-3.17 0-1.5.79-2.24 1.07-2.55.28-.3.6-.38.8-.38l.58.01c.18.01.44-.07.68.52l.94 2.28c.1.2.16.44.03.68l-.36.6c-.12.17-.25.38-.11.62.14.24.62 1.02 1.32 1.65.9.8 1.66 1.05 1.9 1.17.24.12.38.1.52-.06l.75-.87c.19-.24.35-.19.59-.1l1.68.79c.24.12.4.18.46.28.06.1.06.6-.18 1.28Z" />
  </svg>
);

const ShopDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("product");
  const [listings, setListings] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/shops/${slug}`)
      .then((r) => {
        setShop(r.data);
        setFollowing(r.data.is_following);
        setFollowers(r.data.follower_count);
        if ((r.data.product_count || 0) === 0 && (r.data.service_count || 0) > 0) setTab("service");
      })
      .catch(() => toast.error("Shop not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  const loadListings = useCallback(() => {
    if (!shop?.user_id) return;
    setListLoading(true);
    api
      .get(`/listings?owner_id=${shop.user_id}&type=${tab}&limit=48`)
      .then((r) => setListings(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch(() => setListings([]))
      .finally(() => setListLoading(false));
  }, [shop, tab]);

  useEffect(() => { loadListings(); }, [loadListings]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow shops"); return; }
    try {
      const { data } = await api.post(`/shops/${shop.user_id}/follow`);
      setFollowing(data.following);
      setFollowers(data.follower_count);
      toast.success(data.following ? "Following — you'll see their new items" : "Unfollowed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>;
  }
  if (!shop) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-center">
        <div>
          <Store className="w-12 h-12 mx-auto text-black/15 mb-3" />
          <div className="font-semibold">Shop not found</div>
          <Link to="/shops" className="text-sm text-[#C05A3C] underline mt-2 inline-block">Browse all shops</Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.user_id === shop.user_id;

  if (!shop.is_open && !isOwner) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-5" data-testid="shop-not-open">
        <div className="text-center max-w-md">
          <Store className="w-12 h-12 mx-auto text-black/15 mb-3" />
          <div className="font-display text-2xl">This seller hasn't opened a shop yet</div>
          <p className="text-sm text-muted-foreground mt-2">
            You can still find their items across AfriMarket.
          </p>
          <Link to="/browse" className="mt-5 inline-block px-5 py-2.5 rounded-full bg-[#C05A3C] text-white text-sm font-semibold">
            Browse products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="shop-detail-page">
      {isOwner && !shop.is_open && (
        <div className="bg-[#FEF3C7] border-b border-[#F0D48A] text-[#7A5B12] text-sm" data-testid="shop-closed-banner">
          <div className="container-narrow py-2.5 flex items-center justify-between gap-3">
            <span>Your shop is <strong>not open</strong> — it won't appear in the Shops directory yet.</span>
            <Link to="/dashboard/shop" className="font-semibold underline shrink-0">Open my shop →</Link>
          </div>
        </div>
      )}
      {/* Banner */}
      <div className="h-40 sm:h-56 bg-gradient-to-br from-[#C05A3C]/30 via-[#E8C07D]/30 to-[#2A3D36]/20 relative overflow-hidden">
        {shop.banner_url && <img src={shop.banner_url} alt="" className="w-full h-full object-cover" />}
      </div>

      <div className="container-narrow">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-14 relative">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-4 border-[#FAF7F2] bg-[#1A2024] overflow-hidden flex items-center justify-center shrink-0">
            {shop.logo_url ? (
              <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-display text-4xl">{(shop.name || "S").slice(0, 1)}</span>
            )}
          </div>
          <div className="flex-1 pb-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl tracking-tight" data-testid="shop-name">{shop.name}</h1>
              {shop.owner_verified_badge && <BadgeCheck className="w-5 h-5 text-[#C05A3C]" title="Verified seller" />}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
              {shop.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {shop.location}</span>}
              <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {followers} followers</span>
              <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {shop.listing_count} items</span>
            </div>
          </div>
          <div className="flex items-center gap-2 pb-1">
            {isOwner ? (
              <Link to="/dashboard/shop">
                <Button variant="outline" data-testid="shop-edit-btn" className="border-[#1A2024] rounded-full">
                  <Settings className="w-4 h-4 mr-1.5" /> Edit shop
                </Button>
              </Link>
            ) : (
              <Button
                onClick={toggleFollow}
                data-testid="shop-follow-btn"
                className={following ? "rounded-full bg-white text-[#C05A3C] border border-[#C05A3C] hover:bg-[#C05A3C]/5" : "rounded-full bg-[#C05A3C] hover:bg-[#D46B4E] text-white"}
              >
                <Heart className={`w-4 h-4 mr-1.5 ${following ? "fill-[#C05A3C]" : ""}`} /> {following ? "Following" : "Follow"}
              </Button>
            )}
          </div>
        </div>

        {shop.tagline && <p className="mt-4 text-base text-foreground/90 font-sans-ui">{shop.tagline}</p>}
        {shop.bio && <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line max-w-2xl">{shop.bio}</p>}

        {/* Socials */}
        {(shop.whatsapp || shop.instagram) && (
          <div className="mt-4 flex items-center gap-2">
            {shop.whatsapp && (
              <a href={`https://wa.me/${(shop.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer"
                data-testid="shop-whatsapp" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1FB155]">
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {shop.instagram && (
              <a href={`https://instagram.com/${(shop.instagram || "").replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer"
                data-testid="shop-instagram" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/15 text-sm font-medium hover:bg-black/5">
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-black/10">
          {[
            { k: "product", label: "Products", icon: Package, n: shop.product_count },
            { k: "service", label: "Services", icon: Briefcase, n: shop.service_count },
          ].map(({ k, label, icon: Icon, n }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`shop-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${tab === k ? "border-[#C05A3C] text-[#C05A3C]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-4 h-4" /> {label} <span className="text-xs opacity-70">({n})</span>
            </button>
          ))}
        </div>

        <div className="py-8">
          {listLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-14 text-muted-foreground" data-testid="shop-listings-empty">
              No {tab === "product" ? "products" : "services"} in this shop yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-listings-grid">
              {listings.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopDetail;
